import React from 'react'
import { Box, Chip, Typography } from '@mui/material'
import { Bill } from '../../types/bills'
import { getCurrency, getShortFormDate } from '../../helpers/stringHelpers'

interface BillPaymentHistoryProps {
    bill: Bill
}

const methodLabel = (method: string) =>
    method ? method.charAt(0).toUpperCase() + method.slice(1).toLowerCase() : 'Payment'

const statusColor = (status: string, voided: boolean) => {
    if (voided || status === 'failed') {
        return { bg: 'rgba(255,68,68,0.16)', color: '#ff6b6b' }
    }
    if (status === 'succeeded') {
        return { bg: 'rgba(68,255,68,0.12)', color: '#66bb6a' }
    }
    return { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)' }
}

export const BillPaymentHistory = ({ bill }: BillPaymentHistoryProps) => {
    const payments = bill.payments || []

    return (
        <Box>
            <Typography
                variant="subtitle1"
                sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 700, mb: 1.25 }}
            >
                Payments
            </Typography>
            {payments.length === 0 ? (
                <Box
                    sx={{
                        px: 1.75,
                        py: 1.5,
                        borderRadius: 1.5,
                        border: '1px dashed rgba(255,255,255,0.12)',
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: '0.85rem',
                    }}
                >
                    No payments recorded
                </Box>
            ) : (
                <Box
                    sx={{
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(255,255,255,0.03)',
                    }}
                >
                    {payments.map((payment) => {
                        const colors = statusColor(payment.status, payment.is_voided)
                        return (
                            <Box
                                key={payment.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    gap: 1.5,
                                    px: 1.75,
                                    py: 1.35,
                                    borderBottom: '1px solid rgba(255,255,255,0.05)',
                                    '&:last-child': { borderBottom: 'none' },
                                    opacity: payment.is_voided ? 0.65 : 1,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: 'rgba(255,255,255,0.9)',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {getShortFormDate(payment.payment_date)}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: 0.75,
                                            mt: 0.75,
                                        }}
                                    >
                                        <Chip
                                            label={methodLabel(payment.payment_method)}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                backgroundColor: 'rgba(255,255,255,0.08)',
                                                color: 'rgba(255,255,255,0.75)',
                                                fontSize: '0.68rem',
                                                fontWeight: 600,
                                            }}
                                        />
                                        <Chip
                                            label={
                                                payment.status.charAt(0).toUpperCase() +
                                                payment.status.slice(1)
                                            }
                                            size="small"
                                            sx={{
                                                height: 20,
                                                backgroundColor: colors.bg,
                                                color: colors.color,
                                                fontSize: '0.68rem',
                                                fontWeight: 700,
                                            }}
                                        />
                                        {payment.is_voided && (
                                            <Chip
                                                label="Voided"
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    backgroundColor: 'rgba(255,68,68,0.16)',
                                                    color: '#ff6b6b',
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255,255,255,0.9)',
                                        fontWeight: 700,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {getCurrency(payment.amount)}
                                </Typography>
                            </Box>
                        )
                    })}
                </Box>
            )}
        </Box>
    )
}
